import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { Brain, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeColor = (type) => {
  switch (type) {
    case "ubl": return "bg-blue-100 text-blue-700";
    case "cii": return "bg-purple-100 text-purple-700";
    case "cdar": return "bg-emerald-100 text-emerald-700";
    default: return "bg-slate-100 text-slate-700";
  }
};

export const AdminAiAnalyses = () => {
  const [data, setData] = useState({ analyses: [], total: 0, pages: 1, top_rules: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);

  const fetchAnalyses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/ai-analyses?page=${page}&limit=20`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch AI analyses:", err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  return (
    <div data-testid="admin-ai-analyses">
      {/* Frequent rules */}
      {data.top_rules?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="ai-analyses-top-rules">
          <p className="text-sm font-semibold text-slate-700 mb-3">Erreurs les plus fréquentes (règles analysées par l'IA)</p>
          <div className="flex flex-wrap gap-2">
            {data.top_rules.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-xs font-mono text-indigo-700">
                {r.rule}
                <span className="bg-indigo-600 text-white rounded-full px-1.5 py-0 text-[10px] font-sans font-semibold">{r.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
          </div>
        ) : data.analyses.length === 0 ? (
          <div className="p-8 text-center text-slate-500" data-testid="ai-analyses-empty">
            Aucune analyse IA enregistrée pour le moment.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Fichier</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Utilisateur</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Date (Paris)</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Modèle</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Détail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.analyses.map((a) => (
                  <Fragment key={a.id}>
                    <tr className="hover:bg-slate-50" data-testid={`ai-analysis-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-800 truncate max-w-[220px]">{a.filename}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColor(a.validation_type)}`}>
                          {a.validation_type?.toUpperCase() || "UBL"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{a.user_email || "-"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{a.created_at}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs font-mono">{a.model}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                          data-testid={`ai-analysis-toggle-${a.id}`}
                        >
                          {expanded === a.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          {expanded === a.id ? "Masquer" : "Voir Q/R"}
                        </button>
                      </td>
                    </tr>
                    {expanded === a.id && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-slate-50">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Question envoyée à l'IA</p>
                              <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 whitespace-pre-wrap max-h-80 overflow-y-auto text-slate-600">{a.question}</pre>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
                                <Brain className="h-3.5 w-3.5 text-indigo-500" /> Réponse de l'IA
                              </p>
                              <pre className="text-xs bg-indigo-50/50 border border-indigo-100 rounded-lg p-3 whitespace-pre-wrap max-h-80 overflow-y-auto text-slate-700">{a.answer}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="ai-analyses-prev">
            <ChevronLeft className="h-4 w-4" /> Précédent
          </Button>
          <span className="text-sm text-slate-600">Page {page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} data-testid="ai-analyses-next">
            Suivant <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
